import { CaretDownIcon } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { OverflowFadeText } from './OverflowFadeText';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useAiSessionTitle } from '@/hooks/chat/useAiSessionTitle';
import { useResolveCreateParams } from '@/hooks/create/useResolveCreateParams';
import { useActivePlan } from '@/hooks/useActivePlan';
import { peekDiscoverSessionId } from '@/lib/chat/discover-session-id';
import {
  BREADCRUMB_LEAF_CLASS,
  BREADCRUMB_ROOT_CLASS,
  PAGE_HEADER_BREADCRUMB_LIST,
} from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

interface RouteBreadcrumbProps {
  /**
   * When provided, the title leaf is wrapped in a button-like span with a
   * caret to signal an attached popover. The parent component handles the
   * actual popover (PageHeaderManager wires the conversation/plan switcher).
   */
  hasSwitcher?: boolean;
  /**
   * Forces the title leaf to render via the supplied wrapper. Used by the
   * switcher to mount the title as a Popover trigger without coupling this
   * component to switcher concerns.
   */
  renderTitle?: (titleNode: React.ReactNode) => React.ReactNode;
  /**
   * Overrides the derived leaf text with data the CALLER already loaded.
   *
   * Every other branch below derives its leaf from a small fixed set of data
   * hooks this component owns (`useActivePlan`, `useAiSessionTitle`) — that
   * works because those are the only dynamic names the app needed a crumb for.
   * A campaign name is a different case: it comes from whichever provider
   * query the page itself is already running (Google vs Meta), and teaching
   * this component to know about paid-campaign fetching would be the wrong
   * direction of coupling. `leafOverride` is the escape hatch — same idea as
   * `renderTitle`, which already lets a caller take over rendering without
   * this component knowing about switchers.
   */
  leafOverride?: string;
  /** A small icon rendered before the leaf text — e.g. a provider logo. */
  leafIcon?: React.ReactNode;
  /**
   * The segments BETWEEN the root and the leaf, in order. Empty by default,
   * which is every route this component serves through `PageHeaderManager`.
   *
   * 🔴 WHY THIS EXISTS AT ALL. A two-segment crumb was enough for every routed
   * page, so the third level of an object had nowhere to go — and both levels
   * of the Brief object hand-rolled their own `<Link>` + `/` runs instead
   * (`BriefDetailPage`, `BriefConceptPage`). Measured on the live routes at
   * 1440x900 before this prop existed, against `/personas` and `/board` — two
   * pages whose crumbs come from HERE:
   *
   *              root                    separator            leaf
   *   canonical  14px/500/-0.35px        14px/500/-0.35px     14px/500, gaps 6/6
   *   Brief      16px/400/-0.4px         16px/400/normal      14px/500, gaps 6/16
   *
   * Three compounding differences on the root — +2px, −100 weight, and a
   * different tracking — which is exactly the shape skill rule 15 records as
   * unreadable by eye and obvious once measured. The hand-rolled crumbs also
   * rendered zero `<li>` inside no `<nav aria-label="breadcrumb">`, so they
   * were not breadcrumbs to assistive tech at all.
   *
   * The fix is one component for every crumb in the app, which meant teaching
   * this one the depth it was missing rather than leaving two callers to
   * approximate it — skill rule 27's ruling on `InspectorBreadcrumb`, whose
   * 2-item ceiling silently drops middles, applied before it bites here too.
   *
   * Hidden below `md`, the width the Concept dialog's hand-rolled version
   * already chose: three segments plus a ✕ do not fit a 640px bar, and the
   * parent is still one Back away.
   */
  trail?: { label: string; href: string }[];
}

/**
 * The object glyph a route's ROOT crumb carries, keyed by the root's own href.
 *
 * 🔴 Why this exists: `PageHeaderManager`'s `OBJECT_PAGE_HEADERS` renders
 * the plain label for a top-level object page (`/campaigns` shows the
 * orange campaigns tile), but a breadcrumb rendered on a DETAIL route under
 * that same object rendered the root as bare text — so the glyph vanished the
 * moment you navigated in, and reappeared when you went back. Founder,
 * 2026-08-18: "do not see that campaigns has an icon next to it because it
 * comes from the sidebar... we leave that icon, now we don't move that."
 *
 * The competitor does the same thing deliberately — the catalog records it as
 * "the ancestor carries the object glyph, the leaf carries the provider logo".
 * Keyed by `rootHref` so a root can never disagree with the object page it
 * points at.
 */

const SEPARATOR = <BreadcrumbSeparator className="text-muted-foreground/40" />;

/**
 * ⚠️ NO `text-sm font-medium` HERE — and that is the fix, not an omission.
 *
 * `PageHeader`'s `<h1>` owns the page-title scale and declares it RESPONSIVELY:
 * `text-base font-semibold text-foreground/90` on mobile, stepping down to
 * `md:text-sm md:font-medium md:text-foreground/80` on desktop. Every crumb
 * rendered inside that h1 used to restate the DESKTOP half of that pair as a
 * flat constant, which silently overrode the mobile half.
 *
 * Measured on prod at a 285px content viewport, 2026-07-25:
 *
 *   /calendar   h1 16px/600 → renders 16px/600  ✓ (plain string title)
 *   /discover   h1 16px/600 → renders 16px/600  ✓ (plain string title)
 *   /library    h1 16px/600 → renders 15px/500  ✗ (breadcrumb overrode it)
 *   /insights   h1 16px/600 → renders 15px/500  ✗ (inline span overrode it)
 *
 * So the mobile title treatment was working exactly as designed and was being
 * cancelled, page by page, by every title that happened to be a React node
 * rather than a string ("the pageheader title is supposed to be changed on
 * mobile for all pages… im confused why that isnt working here" — founder,
 * 2026-07-25). Inheriting is also simply correct: the h1 is the title, and a
 * crumb inside it has no business declaring how big the title is.
 *
 * What DOES stay is the colour distinction, which carries meaning the h1 cannot
 * know — but only where it's actually meaningful: a root shown ALONGSIDE a
 * visible leaf is muted (it's the ancestor, the leaf is "you are here"); a
 * root shown ALONE, with no leaf ever rendered, IS the page's title and reads
 * at full contrast like any other plain-string title. See `rootColorClass`
 * below, computed per-route from `config.showLeaf`/`detailLeaf` rather than
 * hardcoded into this constant.
 */
const LINK_CLASS = BREADCRUMB_ROOT_CLASS;
// Scale values only. The previous `max-w-[280px] sm:max-w-[420px]` trio were
// arbitrary values, and 280px was WIDER than the 266px the mobile header slot
// actually offers at 390px — so `justify-center` centred an over-wide child, it
// overhung ~7px each side, and the leading character was clipped off the left.
const TITLE_CLASS = cn(
  BREADCRUMB_LEAF_CLASS,
  'inline-flex min-w-0 max-w-56 sm:max-w-sm md:max-w-md items-center gap-1.5',
);

/**
 * The one place the scale IS restated, and it has to be.
 *
 * The shared `BreadcrumbList` primitive hardcodes `text-sm`, and it has eight
 * other consumers (help, discover library, style guide, …) whose sizing is none
 * of this component's business — so the primitive is left alone and overridden
 * here instead. The override is the h1's own responsive pair, not a constant:
 * 16px/600 on mobile, 14px/500 from `md:`. Every crumb below inherits it.
 *
 * A regression test asserts no descendant re-declares an unprefixed size — that
 * test is what caught this list, after the two obvious offenders were fixed.
 */
export function RouteBreadcrumb({
  hasSwitcher,
  renderTitle,
  leafOverride,
  leafIcon,
  trail,
}: RouteBreadcrumbProps): JSX.Element | null {
  const location = useLocation();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const isCreateRoute = location.pathname === '/';
  const isPlansList = location.pathname === '/projects';
  const isPlanDetail = /^\/projects\/[^/?#]+/.test(location.pathname);
  // Flattened 2026-08-15 from `/creatives/studio` etc. — the sidebar stopped
  // nesting anything under a "Creatives" parent on 2026-08-12, and the URL
  // namespace (and this breadcrumb's root label) kept implying a hierarchy
  // the nav no longer has. `/creatives/*` redirects were removed outright
  // 2026-08-23 (dead routes, pre-launch), so this component never needs to
  // recognise the old prefix.
  const isCreativesStudio = location.pathname.startsWith('/studio');
  const isCreativesPersonas = location.pathname.startsWith('/personas');
  const isCreativesBriefs = location.pathname.startsWith('/briefs');
  const isCreatives = isCreativesStudio || isCreativesPersonas || isCreativesBriefs;
  const isAdCopy = location.pathname === '/ad-copy';
  const isWorkflows = location.pathname === '/workflows';
  // The three idea surfaces. `DiscoverNav`'s tab strip already presents these
  // as one object in three views, so the crumb has to agree — see the config
  // branch below for why they previously did not.
  const isDiscoverAds = location.pathname === '/discover/ads';
  const isDiscoverNews = location.pathname === '/discover';
  const isBoard = location.pathname === '/board';
  // A campaign DETAIL route, never the list (`/campaigns`) or its siblings
  // (`/campaigns/drafts`, `/campaigns/launches`, `/campaigns/google/new`,
  // `/campaigns/meta/new`) — those don't match `google-ads/` or `meta-ads/`.
  // Ad groups and ads have no routes of their own (see
  // `CampaignHierarchyPanel.tsx`'s own doc comment), so this never needs a
  // 3rd/4th segment — depth past the campaign is a tab, not a crumb.
  const isCampaignDetail = /^\/campaigns\/(google-ads|meta-ads)\//.test(location.pathname);

  const createParams = useResolveCreateParams();
  const planIdForCreate = isCreateRoute ? (createParams.planId ?? undefined) : undefined;
  // PlanDetailView URL is `/projects/<projectId>?planId=<projectId>`.
  // Prefer the explicit `?planId=` query param so the lookup hits; fall back
  // to the path param for legacy URLs without `?planId=`.
  const planIdForDetail = isPlanDetail
    ? (searchParams.get('planId') ?? params.projectId ?? undefined)
    : undefined;
  const planIdForLookup = planIdForCreate ?? planIdForDetail;

  const { activePlan } = useActivePlan(false, planIdForLookup);

  // Reactive session id source:
  // - On `/?resumeSession=X` (Documents tab "Open in chat", deep links) the
  //   resolved param IS the active session.
  // - Otherwise fall back to the cached discover-flow session id.
  // The hook re-queries when the id changes, so the breadcrumb title updates
  // live as the user navigates between sessions.
  const discoverSessionId = peekDiscoverSessionId();
  const activeSessionId =
    isCreateRoute && !planIdForCreate ? (createParams.resumeSession ?? discoverSessionId) : null;
  const sessionTitle = useAiSessionTitle(activeSessionId);

  const conversationTitle = useMemo<string | null>(() => {
    if (isCreateRoute) {
      if (planIdForCreate) return activePlan?.name ?? 'Loading…';
      // Deliberately NOT falling back to "New conversation": on the empty state
      // there is no conversation, so that label names a thing that does not
      // exist. Returning null renders no crumb at all, which is exactly what
      // mobile has always shipped — and the hero ("Good evening, …") is the
      // page's real title. Once a session has a name, that name IS the crumb.
      return sessionTitle ?? null;
    }
    if (isPlanDetail) return activePlan?.name ?? 'Loading…';
    return null;
  }, [isCreateRoute, isPlanDetail, planIdForCreate, activePlan?.name, sessionTitle]);

  // Compute the route's breadcrumb shape.
  const config = useMemo(() => {
    if (isCreateRoute) {
      // The home chat renders NO title (see PageHeaderManager). It carried
      // "Create › New conversation ⌄" — a root whose href was `/?new=true`, i.e.
      // a reset dressed as navigation, over a leaf naming a conversation that
      // does not exist on the empty state. Substituting the session name was
      // tried and read worse: at any real length it dominated the bar, and the
      // conversation is already on screen.
      //
      // Project-bound chats still get a crumb via the isPlanDetail branch.
      if (!planIdForCreate) return null;
      return {
        rootLabel: 'Projects',
        rootHref: '/projects',
        showLeaf: true,
        detailLeaf: true,
      } as const;
    }

    if (isPlansList) {
      // Projects (no slash, no leaf, no switcher).
      return {
        rootLabel: 'Projects',
        rootHref: null,
        showLeaf: false,
      } as const;
    }
    if (isPlanDetail) {
      // Projects / {project.name ▾}
      return {
        rootLabel: 'Projects',
        rootHref: '/projects',
        showLeaf: true,
        // Mobile shows the LEAF (project name), not the root — the header's ←
        // back arrow already carries "Projects" (Claude pattern: centered name).
        detailLeaf: true,
      } as const;
    }
    if (isAdCopy) {
      return {
        rootLabel: 'Briefs',
        rootHref: '/briefs',
        showLeaf: true,
        leafLabel: 'Ad copy',
        detailLeaf: true,
      } as const;
    }
    if (isCreatives) {
      // Briefs / Studio / Personas each name THEMSELVES as the root now —
      // flattened 2026-08-15, no "Creatives" ancestor left to point at
      // (`/creatives` only survives as a redirect; see
      // `CreativesLegacyRedirects.tsx`). Assets isn't handled here at all:
      // `PageHeaderManager` renders it as a plain string title with no
      // breadcrumb, matching Insights/Discover's own top-level treatment, so
      // this component is never asked for an Assets config.
      if (isCreativesBriefs) {
        return {
          rootLabel: 'Briefs',
          rootHref: '/briefs',
          showLeaf: true,
          leafLabel: 'Briefs',
          detailLeaf: true,
        } as const;
      }
      if (isCreativesStudio) {
        return {
          rootLabel: 'Studio',
          rootHref: '/studio',
          showLeaf: true,
          leafLabel: 'Studio',
          detailLeaf: true,
        } as const;
      }
      // Personas — reached from Brief attachment, not a top-level nav
      // destination, so it still names Briefs as its ancestor.
      return {
        rootLabel: 'Briefs',
        rootHref: '/briefs',
        showLeaf: true,
        leafLabel: 'Personas',
        detailLeaf: true,
      } as const;
    }
    if (isWorkflows) {
      // The sidebar nests "Workflow templates" under the Projects group, so the
      // breadcrumb mirrors that hierarchy: Projects / Workflows (same shape as
      // Library / Faces and Projects / {project name}). Previously this was a
      // single dead "Workflows" crumb, which disagreed with the sidebar nesting.
      // detailLeaf: unlike Library (root label IS the page name), "Projects" is
      // NOT this page's name — without detailLeaf the default mobile render
      // (root only) showed a bare "Projects" title on the Workflows page, with
      // no back arrow to supply the missing context. Mobile shows the leaf
      // ("Workflows"); desktop shows the full "Projects / Workflows" path.
      return {
        rootLabel: 'Projects',
        rootHref: '/projects',
        showLeaf: true,
        leafLabel: 'Workflows',
        detailLeaf: true,
      } as const;
    }

    // THE IDEA SURFACES — Discover / News · Ads · Content ideas.
    //
    // Discover is ONE object in three views and `DiscoverNav` says so, but the
    // header used to disagree three different ways: `/discover` rendered a
    // plain "Discover" (root and view collapsed into one word), `/board`
    // rendered a plain "Content ideas" with no ancestor at all, and
    // `/discover/ads` rendered nothing, because `PageHeaderManager` routes
    // `/discover/*` children through `customTitle` and the page set none.
    //
    // `detailLeaf: true` on all three for the same reason `/workflows` uses it:
    // "Discover" is the GROUP, not any one page's name, so on mobile the crumb
    // shows the leaf the user is actually on ("Ads") rather than a bare
    // ancestor, while desktop keeps the full path.
    //
    // `/board` names Discover as its root despite living outside the
    // `/discover` URL namespace — the sidebar carries ONE row for all three
    // (`match: ['/discover', '/board']` in `app-sidebar.tsx`), so the hierarchy
    // is real even though the URL does not spell it.
    if (isDiscoverNews || isDiscoverAds || isBoard) {
      return {
        rootLabel: 'Discover',
        rootHref: '/discover',
        showLeaf: true,
        leafLabel: isDiscoverAds ? 'Ads' : isBoard ? 'Content ideas' : 'News',
        detailLeaf: true,
      } as const;
    }

    if (isCampaignDetail) {
      // Campaigns / [provider logo] <campaign name> — leaf text comes from
      // `leafOverride`, which the page sets once its own campaign query
      // resolves. Fixed a real defect: neither detail page ever fed this
      // component anything, so `PageHeaderManager`'s `/campaigns` branch fell
      // through to its flat `'Campaigns'` string no matter how deep you
      // navigated (founder, 2026-08-18 — "I clicked into a campaign... it
      // just says 'Campaigns' at the top").
      return {
        rootLabel: 'Campaigns',
        rootHref: '/campaigns',
        showLeaf: true,
        detailLeaf: true,
      } as const;
    }

    return null;
  }, [
    isCreateRoute,
    isPlansList,
    isPlanDetail,
    isCreatives,
    isAdCopy,
    isCreativesBriefs,
    isCreativesStudio,
    isWorkflows,
    isDiscoverNews,
    isDiscoverAds,
    isBoard,
    isCampaignDetail,
    planIdForCreate,
  ]);

  if (!config) return null;

  // Muted-vs-full-contrast is a "you are here" cue — it only means something as
  // a CONTRAST relationship between two visible segments. A root shown ALONE,
  // with no leaf ever rendered beside it, IS the page's title and should read
  // exactly like any other plain-string page title (Insights, Products, "Bull
  // core series…" in a normal — not-in-a-project — Claude.ai chat), not muted.
  // Only genuinely nested cases (a leaf visible alongside it) earn the muted
  // treatment, matching how Claude's own UI dims the ancestor once you're
  // inside a Project (bug report, 2026-07-27 — the fix above made /projects
  // and /library match EACH OTHER, but both still read muted next to Insights'
  // plain, full-contrast title; this makes the "root alone" case match too).
  const isDetailLeaf = Boolean((config as { detailLeaf?: boolean }).detailLeaf);
  const rootColorClass = !config.showLeaf
    ? // e.g. /projects — no leaf exists at all; root IS the whole title.
      cn(BREADCRUMB_LEAF_CLASS, 'hover:text-foreground transition-colors')
    : isDetailLeaf
      ? // e.g. /projects/:id, /workflows — root only ever renders paired with a
        // visible leaf (desktop; mobile hides it and shows the leaf alone).
        LINK_CLASS
      : // e.g. /library — root renders ALONE on mobile (leaf hidden below `sm:`)
        // but paired with a leaf from `sm:` up. Full contrast until paired.
        cn(
          BREADCRUMB_LEAF_CLASS,
          'hover:text-foreground transition-colors sm:text-muted-foreground',
        );

  const leafText = leafOverride || conversationTitle || (config as any).leafLabel;
  const titleNode =
    config.showLeaf && leafText ? (
      <span className={TITLE_CLASS}>
        {leafIcon}
        {/* `block` is load-bearing, not decoration: OverflowFadeText renders a
            <span>, and clientWidth/scrollWidth are BOTH 0 on display:inline, so
            its overflow check never fires and the fade never appears. It
            happens to survive here because a flex child is auto-blockified —
            i.e. it works by accident, and stops the moment the parent stops
            being a flex container. */}
        <OverflowFadeText className="block flex-1">{leafText}</OverflowFadeText>
        {hasSwitcher ? (
          <CaretDownIcon
            aria-hidden="true"
            className="size-3.5 text-muted-foreground/60 shrink-0"
          />
        ) : null}
      </span>
    ) : null;

  // The leading "Plans" / "Create" segment carries NO glyph. It used to mirror
  // the one `PageHeaderManager` rendered, precisely so the icon would not
  // appear and disappear as you navigated in and out of an object. That
  // invariant still holds — both sides dropped it together on 2026-08-24. See
  // PageHeaderManager for the reasoning: the tile is for choosing among
  // options, and a breadcrumb is a statement about where you already are.
  const rootContent = config.rootLabel;

  const rootSegment = config.rootHref ? (
    <BreadcrumbLink asChild>
      <Link to={config.rootHref} className={rootColorClass}>
        {rootContent}
      </Link>
    </BreadcrumbLink>
  ) : (
    <BreadcrumbPage className={cn(rootColorClass, 'font-normal')}>{rootContent}</BreadcrumbPage>
  );

  const leafItem =
    config.showLeaf && titleNode ? (
      <BreadcrumbItem>{renderTitle ? renderTitle(titleNode) : titleNode}</BreadcrumbItem>
    ) : null;

  /**
   * A middle segment is a LINK and wears the ROOT's colour, because that is
   * what it is — an ancestor, not where you are. The leaf is the only segment
   * that reads at full contrast, and that contrast is the whole "you are here"
   * cue (see `rootColorClass` above). `truncate` inside `max-w-40`, which
   * `BREADCRUMB_ROOT_CLASS` already declares, so a long parent name shortens
   * instead of pushing the leaf out of the bar.
   */
  const trailItems = (trail ?? []).map((segment) => (
    <div key={`${segment.href}:${segment.label}`} className="hidden md:contents">
      <BreadcrumbItem className="min-w-0">
        <BreadcrumbLink asChild>
          <Link to={segment.href} className={cn(LINK_CLASS, 'min-w-0 truncate')}>
            {segment.label}
          </Link>
        </BreadcrumbLink>
      </BreadcrumbItem>
      {SEPARATOR}
    </div>
  ));

  // Detail pages (e.g. project): mobile shows ONLY the leaf (name) because the
  // header's ← already says "Projects"; desktop shows the full path.
  if ((config as { detailLeaf?: boolean }).detailLeaf && leafItem) {
    return (
      <Breadcrumb>
        <BreadcrumbList className={PAGE_HEADER_BREADCRUMB_LIST}>
          <div className="contents sm:hidden">{leafItem}</div>
          <div className="hidden sm:contents">
            <BreadcrumbItem>{rootSegment}</BreadcrumbItem>
            {SEPARATOR}
            {trailItems}
            {leafItem}
          </div>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  return (
    <Breadcrumb>
      <BreadcrumbList className={PAGE_HEADER_BREADCRUMB_LIST}>
        {/* Mobile: just the root link. Desktop: full path with the leaf + caret. */}
        <BreadcrumbItem>{rootSegment}</BreadcrumbItem>
        {leafItem ? (
          <div className="hidden sm:contents">
            {SEPARATOR}
            {leafItem}
          </div>
        ) : null}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export default RouteBreadcrumb;
